type Interpolator<T> = (left: T, right: T, point: number) => T;

export const InterpolationMode = {
    LERP: (left: number, right: number, point: number): number => left + (right - left) * point,
    FLOOR: <T>(left: T, right: T, point: number): T => point !== 1 ? left : right,
    ROUND: <T>(left: T, right: T, point: number): T => point < 0.5 ? left : right,
    CEIL: <T>(left: T, right: T, point: number): T => point === 0 ? left : right
}

export const color: Interpolator<string> = function (left: string, right: string, point: number): string {
    var minRGB = (/#([\da-f]{1,2})([\da-f]{1,2})([\da-f]{1,2})/.exec(left) || []).slice(1).map(v => parseInt(v, 16));
    var maxRGB = (/#([\da-f]{1,2})([\da-f]{1,2})([\da-f]{1,2})/.exec(right) || []).slice(1).map(v => parseInt(v, 16));
    return '#' + [0, 0, 0]
        .map((_, index) => minRGB[index] + (maxRGB[index] - minRGB[index]) * point)
        .map(v => v.toString(16))
        .map(v => v.length === 2 ? v : '0' + v)
        .join('');
};

interface Tick<T> {
    point: number;
    result: T;
}

export default class Scale<T = number> extends Array<Tick<T>> {
    static readonly InterpolationMode = InterpolationMode;

    private _unknown: T | undefined;
    private _interpolator: Interpolator<T> = InterpolationMode.ROUND;

    constructor();
    constructor(ticks: number, min: number, max: number, unknown?: T, interpolator?: Interpolator<T>);
    constructor(ticks: { [point: number]: T }, unknown?: T, interpolator?: Interpolator<T>);
    constructor(ticks: Tick<T>[], unknown?: T, interpolator?: Interpolator<T>);
    constructor(ticks: T[], min: number, max: number, unknown?: T, interpolator?: Interpolator<T>);
    constructor(other: Scale<T>);
    constructor(...args: any[]) {
        super();

        let
            ticks = args[0] || 5,
            unknown: T,
            interpolator: Interpolator<T>;
        if (args.length === 1 && typeof args[0] === 'number') {
            return new Array(args[0]) as unknown as Scale<T>;
        }
        if (typeof ticks === 'number') {
            const
                min: number = typeof args[1] === 'number' ? args[1] : 1,
                max: number = typeof args[2] === 'number' ? args[2] : 5;
            ticks = Array(ticks).fill(0).map((_, index) => ({ point: min + (max - min) * index / (ticks - 1), result: min + (max - min) * index / (ticks - 1) }));
            unknown = args[3];
            interpolator = args[4];
        }
        else if (Array.isArray(ticks) && !ticks.every(a => a && a.hasOwnProperty && a.hasOwnProperty('point'))) {
            const
                min: number = typeof args[1] === 'number' ? args[1] : 1,
                max: number = typeof args[2] === 'number' ? args[2] : 5;
            ticks = ticks.map((result, index) => ({ point: min + (max - min) * index / (ticks.length - 1), result: result }));
            unknown = args[3];
            interpolator = args[4];
        }
        else if (!Array.isArray(ticks)) {
            ticks = Object.keys(ticks).map(point => ({ point: +point, result: ticks[point] })).sort((l, r) => l.point - r.point);
            unknown = ticks instanceof Scale ? ticks.unknown : args[1];
            interpolator = ticks instanceof Scale ? ticks._interpolator : args[2];
        }
        else {
            ticks = ticks;
            unknown = args[1];
            interpolator = args[2];
        }

        if (ticks.length < 2) {
            throw new Error('Scale must have at least two ticks.');
        }

        this.push(...ticks);
        this._unknown = unknown;
        this._interpolator = interpolator || InterpolationMode.ROUND;

        if (DEBUG) {
            Object.freeze(this);
        }
    }

    first(): Tick<T> {
        return this[0];
    }

    last(): Tick<T> {
        return this[this.length - 1];
    }

    get min(): number {
        return this.first().point;
    }

    get max(): number {
        return this.last().point;
    }

    get unknown(): T | undefined {
        return this._unknown;
    }

    get points(): number[] {
        return this.map(tick => tick.point);
    }

    get results(): T[] {
        return this.map(tick => tick.result);
    }

    get step(): number {
        if (DEBUG && !this.every((_, index: number) => index >= this.length || this[index + 2].point - this[index + 1].point === this[index + 1].point - this[index].point)) {
            throw new Error("Steps are different for different ticks.");
        }
        return this[1].point - this[0].point;
    }

    interpolate(point: number): T {
        if (this._unknown === undefined) {
            if (point <= this.min) {
                return this.first().result;
            }
            if (point >= this.max) {
                return this.last().result;
            }
        }
        else if (point < this.min || point > this.max) {
            return this._unknown;
        }

        for (let i = 0; i < this.length - 1; i++) {
            if (point === this[i].point) {
                return this[i].result;
            }
            if (point > this[i].point && point < this[i + 1].point) {
                return this._interpolator(this[i].result, this[i + 1].result, (point - this[i].point) / (this[i + 1].point - this[i].point));
            }
            if (point === this[i + 1].point){
                return this[i + 1].result;
            }
        }

        return this.last().result;
    }

    extrapolate(point: number): T {
        if (point <= this.min) {
            return this._interpolator(this[0].result, this[1].result, (point - this[0].point) / (this[1].point - this[0].point));
        }
        if (point >= this.max) {
            return this._interpolator(this[this.length - 2].result, this[this.length - 1].result, (point - this[this.length - 2].point) / (this[this.length - 1].point - this[this.length - 2].point));
        }

        return this.interpolate(point);
    }
}